/**
 * KEEPING THE SIGN-IN ALIVE.
 *
 * MP's widget token is a short-lived access token in localStorage, next to the
 * time it stops being good (`mpp-widgets_ExpiresAfter`). MP's own widgets
 * never send a stale one: before each call they renew it silently, asking
 * MP's authorize endpoint again with the browser's MP session cookie and
 * `state=REAUTH`, which answers with fresh tokens as JSON instead of a page.
 * We sent whatever was stored, so a page left open past the hour looked signed
 * in and then refused with "sign in" errors: Flannel Fest, 2026-10-19, a mum
 * adding her son's friend. This is MP's renewal, copied from MPWidgets.js,
 * sharing its in-flight promise so the header widget and we never renew twice.
 *
 * When MP's own session is gone too, the renewal fails and the stored token is
 * left alone: clearing it would flip the page to signed out and throw away the
 * form. The caller asks the person to sign in instead.
 */
const TOKEN = 'mpp-widgets_AuthToken';
const ID_TOKEN = 'mpp-widgets_IdToken';
const EXPIRES = 'mpp-widgets_ExpiresAfter';
const AUTH_CONFIG = 'https://my.woodsidebible.org/widgets/Api/Auth';

declare global {
  interface Window {
    mppw_refreshTokenPromise?: Promise<void> | null;
  }
}

function read(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    return v && v !== 'null' ? v : null;
  } catch {
    return null;
  }
}

/** True when there is a token and MP's own clock says it is spent (or never said). */
export function tokenIsStale(now: Date = new Date()): boolean {
  if (!read(TOKEN)) return false;
  const expires = read(EXPIRES);
  if (!expires) return true;
  const at = new Date(expires);
  return Number.isNaN(at.getTime()) || now > at;
}

interface AuthConfig {
  signInUrl: string;
  responseType: string;
  scope: string;
  clientId: string;
  redirectUrl: string;
  nonce: string;
}

interface Tokens {
  accessToken?: string;
  idToken?: string;
  expiresIn?: number;
}

async function renew(): Promise<void> {
  if (!read(TOKEN) || !read(ID_TOKEN)) return;
  const config = (await (await fetch(AUTH_CONFIG)).json()) as AuthConfig;
  const url =
    `${config.signInUrl}?response_type=${config.responseType}&scope=${config.scope}` +
    `&client_id=${config.clientId}&redirect_uri=${config.redirectUrl}` +
    `&nonce=${config.nonce}&state=REAUTH`;
  const tokens = (await (await fetch(url, { credentials: 'include' })).json()) as Tokens;
  if (!tokens.accessToken || !tokens.idToken || !tokens.expiresIn) throw new Error('No tokens');
  const expires = new Date();
  expires.setSeconds(expires.getSeconds() + tokens.expiresIn - 60);
  localStorage.setItem(TOKEN, tokens.accessToken);
  localStorage.setItem(ID_TOKEN, tokens.idToken);
  localStorage.setItem(EXPIRES, String(expires));
}

/**
 * Renew now, whatever the clock says. Resolves to the token afterwards: a new
 * one when MP still knew the person, the old one when it did not.
 */
export async function renewMpToken(): Promise<string | null> {
  if (!window.mppw_refreshTokenPromise) {
    window.mppw_refreshTokenPromise = renew()
      .catch(() => undefined)
      .finally(() => {
        window.mppw_refreshTokenPromise = null;
      });
  }
  await window.mppw_refreshTokenPromise;
  return getMpWidgetToken();
}

/** The stored MP widget token, if the visitor signed in through the site header. */
export function getMpWidgetToken(): string | null {
  return read(TOKEN);
}

/** The token to send: renewed first when it has run out. */
export async function freshMpToken(): Promise<string | null> {
  if (window.mppw_refreshTokenPromise) await window.mppw_refreshTokenPromise;
  if (tokenIsStale()) return renewMpToken();
  return getMpWidgetToken();
}

/** Fired on window when a sign-in could not be renewed and the person must sign in again. */
export const MP_SIGN_IN_NEEDED = 'mp-widgets:sign-in-needed';

/**
 * fetch, carrying the visitor's sign-in when there is one. The token is renewed
 * before it is sent when it has run out, and a 401 that slips past the clock is
 * renewed and tried once more. Safe for writes: our API refuses a bad sign-in
 * before it writes anything. A 401 that survives means MP has forgotten the
 * person. For a write, somebody pressed a button and is waiting, so
 * `MP_SIGN_IN_NEEDED` is fired and the page asks them to sign in instead of
 * showing the refusal. A read just comes back 401: a page load must never pop
 * a sign-in box at a visitor whose old token is still lying in storage.
 *
 * `headers` are the caller's own (API key, content type); Authorization is ours.
 */
export async function mpFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const send = (token: string | null) => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    else headers.delete('Authorization');
    return fetch(url, { ...init, headers });
  };
  const token = await freshMpToken();
  const res = await send(token);
  if (res.status !== 401 || !token) return res;
  const renewed = await renewMpToken();
  if (renewed && renewed !== token) {
    const again = await send(renewed);
    if (again.status !== 401) return again;
  }
  const method = (init.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD')
    window.dispatchEvent(new CustomEvent(MP_SIGN_IN_NEEDED));
  return res;
}
